import { Controller } from "@hotwired/stimulus"

// The builder's master list of documents it collects from subs (Larry
// 2026-08-06). Edits autosave on change; adding and removing update the table
// in place — the page never reloads.
export default class extends Controller {
  static targets = ["rows", "row", "newLabel", "newExpires", "status", "removeModal"]
  static values = { createUrl: String, updateUrl: String, templateUrl: String, templateDeleteUrl: String }

  async save(event) {
    const row = event.currentTarget.closest("[data-id]")
    const payload = {}
    row.querySelectorAll("[data-field]").forEach((field) => {
      payload[field.dataset.field] = field.type === "checkbox" ? String(field.checked) : field.value
    })

    const response = await this._send(this._rowUrl(row), "PATCH", payload)
    this._status(response.ok ? "Saved" : (response.body.error || "That couldn't be saved."), response.ok)
  }

  // ── Per-type template file (Larry 2026-08-11) — the GC's own blank the
  // sub downloads, fills in and signs (safety statement, blank W-9). ──
  pickTemplate(event) {
    event.currentTarget.closest("[data-template-cell]")?.querySelector("[data-template-input]")?.click()
  }

  async templatePicked(event) {
    const input = event.currentTarget
    const row = input.closest("[data-id]")
    const file = input.files?.[0]
    input.value = ""
    if (!row || !file) return

    const form = new FormData()
    form.append("file", file)
    const url = this.templateUrlValue.replace("ID", row.dataset.id)
    const response = await fetch(url, {
      method: "POST",
      headers: { Accept: "application/json", "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "" },
      body: form
    })
    let body = {}
    try { body = await response.json() } catch (_e) { body = {} }
    if (response.ok && body.ok) {
      this._renderTemplateCell(row, body.requirement.template_filename)
      this._status("Saved", true)
    } else {
      this._status(body.error || "The template couldn't be uploaded.", false)
    }
  }

  async removeTemplate(event) {
    const row = event.currentTarget.closest("[data-id]")
    if (!row) return
    const url = this.templateDeleteUrlValue.replace("ID", row.dataset.id)
    const response = await this._send(url, "DELETE", {})
    if (response.ok) {
      this._renderTemplateCell(row, null)
      this._status("Saved", true)
    } else {
      this._status(response.body.error || "The template couldn't be removed.", false)
    }
  }

  _renderTemplateCell(row, filename) {
    const cell = row.querySelector("[data-template-cell]")
    if (!cell) return
    const input = cell.querySelector("[data-template-input]")
    const keep = input ? input.outerHTML : ""
    cell.innerHTML = filename
      ? `${keep}<span class="inline-flex items-center gap-1 text-gray-600"><span data-template-name class="max-w-[180px] truncate">${this._escape(filename)}</span></span>
         <button type="button" data-action="company-documents#removeTemplate" class="text-gray-300 hover:text-red-500 cursor-pointer leading-none" title="Remove template">×</button>`
      : `${keep}<button type="button" data-action="company-documents#pickTemplate" class="text-brand hover:underline font-medium cursor-pointer">+ Attach a template for the sub to fill in</button>`
  }

  _escape(value) {
    const div = document.createElement("div")
    div.textContent = value == null ? "" : String(value)
    return div.innerHTML
  }

  async add() {
    const label = this.newLabelTarget.value.trim()
    if (!label) {
      this._status("Give the document a name.", false)
      this.newLabelTarget.focus()
      return
    }

    const response = await this._send(this.createUrlValue, "POST", {
      label, expires: this.newExpiresTarget.value, allow_na: "false", default_state: "off"
    })
    if (!response.ok) {
      this._status(response.body.error || "That couldn't be added.", false)
      return
    }

    this.rowsTarget.appendChild(this._buildRow(response.body.requirement))
    this.newLabelTarget.value = ""
    this._status("Saved", true)
  }

  confirmRemove(event) {
    this._pendingRow = event.currentTarget.closest("[data-id]")
    this.removeModalTarget.classList.remove("hidden")
  }

  hideRemove() {
    this.removeModalTarget.classList.add("hidden")
  }

  async remove() {
    if (!this._pendingRow) return

    const response = await this._send(this._rowUrl(this._pendingRow), "DELETE", {})
    if (response.ok) {
      this._pendingRow.remove()
      this._status("Saved", true)
    } else {
      this._status(response.body.error || "That couldn't be removed.", false)
    }
    this._pendingRow = null
    this.hideRemove()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  // ── Helpers ───────────────────────────────────────────────────────────
  _rowUrl(row) {
    return this.updateUrlValue.replace("ID", row.dataset.id)
  }

  async _send(url, method, payload) {
    const response = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
      },
      body: JSON.stringify(payload)
    })
    let body = {}
    try { body = await response.json() } catch (_error) { body = {} }
    return { ok: response.ok, body }
  }

  // Mirrors the server-rendered row so an added document behaves identically
  // to one that was there on page load.
  _buildRow(requirement) {
    const row = document.createElement("tr")
    row.dataset.id = requirement.id
    row.setAttribute("data-company-documents-target", "row")
    row.innerHTML = `
      <td class="px-3 py-1.5">
        <input type="text" data-field="label" data-action="change->company-documents#save"
               class="w-full rounded-lg border border-transparent hover:border-gray-300 focus:border-brand px-2 py-1.5 text-[13px] focus:outline-none">
        <div class="flex items-center flex-wrap gap-x-4 gap-y-1 mt-0.5 px-2">
          <span data-template-cell class="inline-flex items-center gap-1.5 text-[13px]">
            <input type="file" class="hidden" data-template-input
                   accept=".pdf,.docx,.doc,.xlsx,.png,.jpg,.jpeg"
                   data-action="change->company-documents#templatePicked">
            <button type="button" data-action="company-documents#pickTemplate"
                    class="text-brand hover:underline font-medium cursor-pointer">+ Attach a template for the sub to fill in</button>
          </span>
        </div>
      </td>
      <td class="px-2 py-1.5">${this._select("expires", requirement.expires, [["true", "Yes"], ["false", "No"]])}</td>
      <td class="px-2 py-1.5">${this._select("allow_na", requirement.allow_na, [["true", "Yes"], ["false", "No"]])}</td>
      <td class="px-2 py-1.5">${this._select("default_state", requirement.default_state,
        [["required", "Required"], ["optional", "Optional"], ["off", "Don't ask"]])}</td>
      <td class="px-2 py-1.5 text-right">
        <button type="button" data-action="company-documents#confirmRemove"
                class="text-gray-400 hover:text-red-500 cursor-pointer">&times;</button>
      </td>`
    row.querySelector("input[data-field='label']").value = requirement.label
    return row
  }

  _select(field, current, options) {
    const value = String(current)
    const rendered = options
      .map(([option, label]) => `<option value="${option}"${option === value ? " selected" : ""}>${label}</option>`)
      .join("")
    return `<select data-field="${field}" data-action="change->company-documents#save"
              class="w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-[13px] focus:outline-none focus:border-brand">${rendered}</select>`
  }

  _status(message, ok) {
    this.statusTarget.textContent = message
    this.statusTarget.className = ok ? "text-[13px] text-green-600 mt-2" : "text-[13px] text-red-600 mt-2"
    if (ok) setTimeout(() => { this.statusTarget.textContent = "" }, 2500)
  }
}
